import {
  parseUnavailableApiPayload,
  createUnavailableResult,
} from './unavailableResource';

export function outletSlugFromName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Same store page under two URL shapes (slug vs id). */
export function outletMatchesPathSegment(outlet, segment) {
  if (!outlet || segment == null) return false;
  const needle = String(segment).trim().toLowerCase();
  if (!needle) return false;
  const slug = getOutletPathSlug(outlet).toLowerCase();
  return (
    slug === needle ||
    String(outlet._id || '').toLowerCase() === needle ||
    String(outlet.id || '').toLowerCase() === needle ||
    String(outlet.name || '').trim().toLowerCase() === needle
  );
}

/** Preferred URL segment for a store page (slug from name, else legacy id). */
export function getOutletPathSlug(outlet) {
  if (!outlet) return '';
  const slug = outlet.slug || outletSlugFromName(outlet.name);
  if (slug) return slug;
  return String(outlet.id || outlet._id || '');
}

export function normalizeOutlet(o) {
  const contact = o.contact || {};
  const physicalLocation =
    o.physicalLocation ||
    o.location ||
    [o.address?.street, o.address?.city, o.address?.country].filter(Boolean).join(', ') ||
    '';
  const website = (contact.website || o.websiteUrl || o.storeWebsite || '').trim();
  const name = o.name || '';
  return {
    ...o,
    _id: o._id || o.id,
    id: o.id || o._id,
    logo: o.logo != null ? o.logo : o.storeLogo,
    logoImagekit: o.logoImagekit || o.storeLogoImagekit || '',
    isActive: o.isActive !== false,
    contact,
    physicalLocation,
    websiteUrl: website,
    slug: o.slug || outletSlugFromName(name),
  };
}

export function formatOutletType(type) {
  const map = {
    vendor: 'Verified Partner',
    supplier: 'Supplier',
    retailer: 'Retail',
    manufacturer: 'Manufacturer',
  };
  return map[String(type || '').toLowerCase()] || 'Retail Partner';
}

export function getLogoLabel(outlet) {
  if (outlet.code) return String(outlet.code).replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase();
  const words = String(outlet.name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]} ${words[1]}`.slice(0, 8).toUpperCase();
  }
  return String(outlet.name || 'STORE').slice(0, 8).toUpperCase();
}

export function getFullAddress(outlet) {
  if (outlet.physicalLocation) return String(outlet.physicalLocation).trim();
  if (outlet.location) return String(outlet.location).trim();
  const parts = [
    outlet.address?.street,
    outlet.address?.city,
    outlet.address?.country,
  ].filter(Boolean);
  return parts.join(', ');
}

export function getOutletSubtitle(outlet) {
  const city = outlet.address?.city;
  if (city) return String(city).trim();
  return formatOutletType(outlet.type);
}

export function getVerticalTag(outlet) {
  const desc = String(outlet.description || '').trim();
  if (desc) {
    const line = desc.split(/\n/)[0].trim();
    if (line.length <= 48) return line;
    return `${line.slice(0, 45).trim()}…`;
  }
  const city = outlet.address?.city || outlet.physicalLocation || outlet.location;
  if (city) return String(city).trim();
  return formatOutletType(outlet.type);
}

export function getMaxDiscountFromProducts(products) {
  let max = 0;
  (products || []).forEach((product) => {
    const discount = Number(product.discount) || 0;
    if (discount > max) max = discount;
  });
  return max;
}

/** MongoDB _id is what product.storeId uses; railway UUID is stored on outlet.id. */
export function getStoreApiId(outlet) {
  if (!outlet) return '';
  return String(outlet._id || outlet.id || '');
}

/** Resolve /outlets/:segment from API id, slug, or name (slug URLs before backend slug route ships). */
export async function resolveOutletByPathSegment(api, segment) {
  const needle = String(segment || '').trim();
  if (!needle) return null;

  try {
    const response = await api.get(`/outlets/${encodeURIComponent(needle)}`);
    const vendorData = response?.data ?? response;
    const unavailable = parseUnavailableApiPayload(vendorData);
    if (unavailable) {
      return createUnavailableResult({
        ...unavailable,
        title: unavailable.reason === 'disabled' ? 'Store not available' : 'Store not found',
      });
    }
    if (vendorData && (vendorData._id || vendorData.id || vendorData.name)) {
      const normalized = normalizeOutlet(vendorData);
      if (normalized.isActive === false) {
        return createUnavailableResult({
          reason: 'disabled',
          title: 'Store not available',
          message: `${normalized.name || 'This store'} is not currently available on Raheasy.`,
          name: normalized.name || '',
        });
      }
      return normalized;
    }
  } catch {
    // Try list match below
  }

  try {
    const listRes = await api.get('/outlets');
    const raw = listRes?.data ?? listRes?.outlets ?? listRes;
    const list = Array.isArray(raw) ? raw : [];
    const key = needle.toLowerCase();
    const match = list.map(normalizeOutlet).find((outlet) => {
      const slug = getOutletPathSlug(outlet).toLowerCase();
      return (
        slug === key ||
        String(outlet._id || '').toLowerCase() === key ||
        String(outlet.id || '').toLowerCase() === key ||
        String(outlet.name || '').trim().toLowerCase() === key
      );
    });
    return match || null;
  } catch {
    return null;
  }
}

export function outletMatchesProduct(outlet, product) {
  const vendorMongoId = outlet._id ? String(outlet._id) : '';
  const vendorRailwayId = outlet.id ? String(outlet.id) : '';
  const vendorName = (outlet.name || '').trim().toLowerCase();
  const productStoreId = product.storeId ? String(product.storeId) : '';
  const productStoreName = (product.storeName || '').trim().toLowerCase();
  const productVendorId = product.vendor?.id || product.vendor?._id;
  const productVendorName = (product.vendor?.name || '').trim().toLowerCase();
  return (
    (productStoreId && (productStoreId === vendorMongoId || productStoreId === vendorRailwayId)) ||
    (productStoreName && productStoreName === vendorName) ||
    (productVendorId &&
      (String(productVendorId) === vendorMongoId || String(productVendorId) === vendorRailwayId)) ||
    (productVendorName && productVendorName === vendorName)
  );
}

/** Index outlets by store id / name for O(products) max-discount lookup (not O(products × outlets)). */
function buildOutletDiscountIndex(outlets) {
  const map = new Map();
  const byMongoId = new Map();
  const byRailwayId = new Map();
  const byName = new Map();

  outlets.forEach((outlet) => {
    const key = String(outlet._id || outlet.id || '');
    map.set(key, 0);
    if (outlet._id) byMongoId.set(String(outlet._id), key);
    if (outlet.id) byRailwayId.set(String(outlet.id), key);
    const name = (outlet.name || '').trim().toLowerCase();
    if (name) byName.set(name, key);
  });

  return { map, byMongoId, byRailwayId, byName };
}

function resolveOutletKeyForProduct(index, product) {
  const productStoreId = product.storeId ? String(product.storeId) : '';
  if (productStoreId) {
    const hit =
      index.byMongoId.get(productStoreId) || index.byRailwayId.get(productStoreId);
    if (hit) return hit;
  }

  const productStoreName = (product.storeName || '').trim().toLowerCase();
  if (productStoreName) {
    const hit = index.byName.get(productStoreName);
    if (hit) return hit;
  }

  const productVendorId = product.vendor?.id || product.vendor?._id;
  if (productVendorId) {
    const vid = String(productVendorId);
    const hit = index.byMongoId.get(vid) || index.byRailwayId.get(vid);
    if (hit) return hit;
  }

  const productVendorName = (product.vendor?.name || '').trim().toLowerCase();
  if (productVendorName) return index.byName.get(productVendorName) || null;

  return null;
}

export function buildMaxDiscountByOutlet(outlets, products) {
  const index = buildOutletDiscountIndex(outlets);
  (products || []).forEach((product) => {
    const discount = Number(product.discount) || 0;
    if (discount <= 0) return;
    const outletKey = resolveOutletKeyForProduct(index, product);
    if (!outletKey) return;
    index.map.set(outletKey, Math.max(index.map.get(outletKey) || 0, discount));
  });
  return index.map;
}
